import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import RenterLayout from '../components/layout/RenterLayout';
import { renterService } from '../services/renter.service';
import { buildPublicFileUrl } from '../utils/fileUrl';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80&w=1200';

const getImageUrl = (imageUrl) => {
  if (!imageUrl) return FALLBACK_IMAGE;
  return buildPublicFileUrl(imageUrl);
};

const getRefundClasses = (status) => {
  switch (status) {
    case 'REFUNDED':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    default:
      return 'bg-amber-50 text-amber-700 border-amber-100';
  }
};

const RenterRefunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadRefunds = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await renterService.getRefunds();
        setRefunds(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load refunds.');
      } finally {
        setLoading(false);
      }
    };

    loadRefunds();
  }, []);

  return (
    <RenterLayout
      activePage="Refunds"
      title="Refund Center"
      description="Follow every refund request, understand the reason behind it, and see whether it is still under review or already completed."
    >
      {loading ? (
        <div className="min-h-[320px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="rounded-[1.75rem] border border-red-100 bg-red-50 px-6 py-5 text-red-700 font-semibold">
          {error}
        </div>
      ) : refunds.length === 0 ? (
        <div className="rounded-[1.75rem] border border-gray-100 bg-[#f8fafc] px-8 py-14 text-center">
          <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-black tracking-tight mb-2">No refund activity</h2>
          <p className="text-gray-500 font-medium">Refund requests and processed refunds will appear here when they exist.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {refunds.map((refund) => (
            <div key={refund.id} className="rounded-[1.75rem] border border-gray-100 bg-white overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-[180px_minmax(0,1fr)]">
                <div className="h-full min-h-[220px] bg-[#f8fafc]">
                  <img src={getImageUrl(refund.image)} alt={refund.vehicle} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-2">
                        Refund Ref {refund.id?.slice(0, 8)?.toUpperCase()}
                      </p>
                      <h2 className="text-2xl font-black tracking-tight">{refund.vehicle || 'Refund Request'}</h2>
                    </div>
                    <span className={`rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] ${getRefundClasses(refund.refundStatus)}`}>
                      {refund.refundStatus}
                    </span>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Amount</p>
                      <p className="text-sm font-black text-brand-black">{refund.amount || 'RF 0'}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Reason</p>
                      <p className="text-sm font-semibold text-gray-600">{refund.reason || 'No reason supplied'}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Requested At</p>
                      <p className="text-sm font-semibold text-gray-600">{refund.requestedAt || '-'}</p>
                    </div>
                    <div className="rounded-2xl bg-[#f8fafc] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400 mb-1">Booking</p>
                      <p className="text-sm font-semibold text-gray-600">{refund.bookingId?.slice(0, 8)?.toUpperCase() || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </RenterLayout>
  );
};

export default RenterRefunds;
